import { parseJwt } from '@libs/utils/helpers/parseJwt';
import safestorage from '@libs/utils/helpers/getSafeStorage';

import { ApiAuthResponse, ApiSigninResponse } from '@admission/apis/auth/types';
import { alovaInstanceAuth } from '@admission/apis/auth/alovaInstanceOnboarding';

import { StorageMap } from '@common/constants/StorageMap';
import { handleErrorLog } from '@common/utils/handleErrorLogs';
import { bridge } from '@libs/app-bridge';

import { useUserStore } from '@libs/acp/store/user/useUserStore';

const event = new Event('handleSignOut');

export async function handleRefreshToken() {
  try {
    const { data } = await alovaInstanceAuth.Post<ApiAuthResponse<ApiSigninResponse>>('/v2/public/auth/refresh-token');

    if (!data) {
			handleSignOut();

      return null;
    }

    await setToken(data);
    return data;

  } catch (error) {
    handleErrorLog(error);
		handleSignOut();

    return null;
  }
}

export function handleSignOut() {
  dispatchEvent(event);
}

export function tokenExpired(token?: string) {
  if (!token) return true;

  const { exp } = parseJwt(token);
  const currentDateInTimeStamp = Math.floor(Date.now() / 1000);

  return currentDateInTimeStamp > exp;
}

async function setToken(token: ApiSigninResponse) {
  await safestorage.setItem(StorageMap.Token, token);
  if (bridge.isWebViewBridgeAvailable) {
    await bridge.asyncStorageSetItem(StorageMap.Token, JSON.stringify(token));
  }

	useUserStore.getState().updateACPAuthSession({
		...useUserStore.getState().authenticatedUser.acp.auth,
		access_token: token.access_token,
		refresh_token: token.refresh_token,
		isAuthenticated: true,
	});
}

export async function getToken(): Promise<ApiSigninResponse | null> {
  let token = await safestorage.getItem<ApiSigninResponse>(StorageMap.Token);
  if (!token && bridge.isWebViewBridgeAvailable) {
    const tokenStr = await bridge.asyncStorageGetItem(StorageMap.Token);
    if (typeof tokenStr === 'string') {
      token = JSON.parse(tokenStr) as ApiSigninResponse;
      safestorage.setItem<ApiSigninResponse>(StorageMap.Token, token);
    }
  }

  return token;
}

export function isValidToken(token: ApiSigninResponse) {
  if (!token?.access_token) return false;

  const isAccessTokenExpired = tokenExpired(token.access_token);
  const isRefreshTokenExpired = tokenExpired(token?.refresh_token);
  const isOldPatternToken = token.expires_in_seconds > 3600;

  if (isOldPatternToken) {
    if (isAccessTokenExpired) return false;
    return true;
  }

  if (isAccessTokenExpired) {
    if (isRefreshTokenExpired) return false;
    return true;
  }

  return true;
}

export function shouldRefreshToken(token: ApiSigninResponse) {
  const isAccessTokenExpired = tokenExpired(token.access_token);
  const isOldPatternToken = token.expires_in_seconds > 3600;

  if (isAccessTokenExpired || isOldPatternToken) return true;
  return false;
}
